import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { BoolCell, DateCell, MoneyCell, MutedCell, NameCell, NumberCell, StatusChip } from '@/components/TableCells'
import { DeliveryStatusBadge, deliveryStatusOf } from '@/components/DeliveryStatusBadge'
import { CampaignTimeline } from '@/components/CampaignTimeline'
import { InlineEditBar } from '@/components/InlineEditBar'
import { USER_STATES } from '@/components/UserStateSelect'
import { useInlineSave } from '@/hooks/useInlineSave'
import { useDefaultCurrency } from '../hooks/useDefaultCurrency'
import { isInlineMoney, type InlineEditSpec } from '@/lib/inlineEdit'
import {
  LineItem,
  LineItemObjectives,
  LineItemsApi,
  OBJECTIVE_PRIORITY,
  primaryObjective,
  type LineItemBulkInput,
  type LineItemType,
} from '@/services/lineItems/lineItemsApi'
import { formatNumber, formatCompact } from '../utils/format'
import { TargetingListCell } from '@/components/targeting/TargetingListCell'
import { useNetwork } from '../contexts/NetworkContext'
import { useCurrentUser } from '../contexts/UserContext'
import { isSuperAdmin } from '../data/superAdmins'
import { LineItemTransparencyLink } from '@/components/political/LineItemTransparencyLink'
import { catalogMetrics, statMetricColumns, type StatsRow } from './statsColumns'
import { useStatLabel } from '../hooks/useStatLabel'

/** One line item's metrics, keyed by metric name as the API reports them. */
type LineItemStatsRow = StatsRow

const LINE_ITEM_TYPES: LineItemType[] = ['AUCTION', 'SPONSORSHIP', 'CREATIVE_TAG']

/**
 * A line item's targeting, read and edited from the list. A product re-imposes
 * its own targeting on save, and a political ad that has delivered keeps what
 * it ran with, so both are left to the line item's own page.
 */
function LineItemTargetingCell({ lineItem }: { lineItem: LineItem }) {
  const { t } = useTranslation()
  const politicalLocked =
    !!lineItem.electionId &&
    ((lineItem.renderedDelivery ?? 0) > 0 || (lineItem.impressionDelivery ?? 0) > 0)
  return (
    <TargetingListCell
      queryKey={['lineitem', lineItem.id]}
      queryFn={() => LineItemsApi.getById(lineItem.id)}
      targeting={lineItem.targetingComplete ? lineItem.targeting : undefined}
      save={(targeting) => LineItemsApi.update(lineItem.id, { name: lineItem.name, targeting })}
      readOnlyReason={
        lineItem.axProductId
          ? t('targeting.listProductLocked')
          : politicalLocked
            ? t('targeting.politicalLocked')
            : undefined
      }
    />
  )
}

/**
 * A line item booked against a marketplace product has its price, type and
 * period dictated by that product — the server re-imposes them on every save —
 * so those fields are not offered for editing from the list.
 */
function isProductBacked(li: LineItem): boolean {
  return !!li.axProductId
}

// ─── Objectives cell ───────────────────────────────────────────────────────

function formatObjectives(obj: LineItemObjectives): string {
  if (obj.BUDGET) {
    const amt = obj.BUDGET.amount
    const cur = obj.BUDGET.currency
    return `${cur} ${formatNumber(amt, { maximumFractionDigits: 0 })}`
  }
  if (obj.IMPRESSION) {
    return `${formatCompact(obj.IMPRESSION)} imps`
  }
  if (obj.CLICK) {
    return `${formatCompact(obj.CLICK)} clicks`
  }
  if (obj.VIEWABLE_IMPRESSION) {
    return `${formatCompact(obj.VIEWABLE_IMPRESSION)} viewable`
  }
  return '—'
}

// ─── Editable fields ───────────────────────────────────────────────────────

/** The objective a line item is steered by — impressions when it has none. */
function objectiveTypeOf(li: LineItem): keyof LineItemObjectives {
  return primaryObjective(li.objectives)?.type ?? 'IMPRESSION'
}

/**
 * What each editable column of the list may change, and the payload that
 * changes it. The API leaves out every field a save does not carry, so a
 * patch only ever describes the one field behind its cell — except for the
 * objectives, which the server replaces whole and which are therefore rebuilt
 * from the objectives the line item already has.
 */
function useLineItemEditSpecs(fallbackCurrency: string) {
  const { t } = useTranslation()

  return useMemo(() => {
    const currencyOf = (li: LineItem) =>
      li.bidSpecification?.cpm?.currency ||
      li.bidSpecification?.cpc?.currency ||
      li.objectives?.BUDGET?.currency ||
      fallbackCurrency

    const text = (
      field: 'name' | 'externalReference',
      extra: Partial<InlineEditSpec<LineItem>> = {},
    ): InlineEditSpec<LineItem> => ({
      type: 'text',
      maxLength: 255,
      value: (li) => li[field] || null,
      // Cleared as an empty string rather than null: the API only takes a
      // string here, and an absent value would leave the old one in place.
      patch: (value) => ({ [field]: value === null ? '' : String(value) }),
      ...extra,
    })

    const bid = (field: 'cpm' | 'cpc'): InlineEditSpec<LineItem> => ({
      type: 'money',
      min: 0,
      currency: currencyOf,
      readOnly: isProductBacked,
      value: (li) => li.bidSpecification?.[field] ?? null,
      patch: (value) => ({
        bidSpecification: { [field]: isInlineMoney(value) ? value : null },
      }),
    })

    const period = (field: 'startDate' | 'endDate'): InlineEditSpec<LineItem> => ({
      type: 'date',
      value: (li) => li[field] || null,
      patch: (value) => ({ [field]: value === null ? null : String(value) }),
      validate: (value, li) => {
        if (value === null) return undefined
        const picked = new Date(String(value))
        const other = field === 'startDate' ? li.endDate : li.startDate
        if (!other) return undefined
        const ends = field === 'startDate' ? new Date(other) : picked
        const starts = field === 'startDate' ? picked : new Date(other)
        return ends < starts ? t('inlineEdit.endBeforeStart') : undefined
      },
    })

    const objectives: InlineEditSpec<LineItem> = {
      // A budget objective is an amount of money; every other one is a count.
      type: (li) => (objectiveTypeOf(li) === 'BUDGET' ? 'money' : 'number'),
      min: 0,
      fractionDigits: 0,
      currency: currencyOf,
      value: (li) => primaryObjective(li.objectives)?.target ?? null,
      patch: (value, li) => {
        const type = objectiveTypeOf(li)
        const rest: LineItemObjectives = { ...li.objectives }
        delete rest[type]
        if (value === null) return { objectives: rest }
        return {
          objectives: {
            ...rest,
            [type]: isInlineMoney(value) ? value : Number(value),
          },
        }
      },
    }

    const objectiveType: InlineEditSpec<LineItem> = {
      type: 'select',
      value: (li) => objectiveTypeOf(li),
      options: () =>
        OBJECTIVE_PRIORITY.map((type) => ({
          value: type,
          label: t(`lineItems.objectiveTypes.${type}`, type),
        })),
      // The target follows the line item to its new objective: a budget keeps
      // its currency, and everything else is the same number of events.
      patch: (value, li) => {
        const next = String(value) as keyof LineItemObjectives
        const current = primaryObjective(li.objectives)
        const amount = isInlineMoney(current?.target ?? null)
          ? (current!.target as { amount: number }).amount
          : Number(current?.target ?? 0)
        const rest: LineItemObjectives = { ...li.objectives }
        if (current) delete rest[current.type]
        return {
          objectives: {
            ...rest,
            [next]:
              next === 'BUDGET'
                ? { amount, currency: currencyOf(li) }
                : Math.round(amount),
          },
        }
      },
    }

    const specs: Record<string, InlineEditSpec<LineItem>> = {
      name: text('name', {
        validate: (value) =>
          String(value ?? '').trim() ? undefined : t('common.nameRequired'),
      }),
      externalReference: text('externalReference'),
      objectives,
      objectiveType,
      cpm: bid('cpm'),
      cpc: bid('cpc'),
      startDate: period('startDate'),
      endDate: period('endDate'),
      userState: {
        type: 'select',
        value: (li) => li.userState || null,
        options: () =>
          USER_STATES.map((state) => ({
            value: state,
            label: t(`deliveryStatuses.${state}`, state),
          })),
        patch: (value) => ({ userState: String(value) }),
      },
      objectState: {
        type: 'select',
        value: (li) => li.objectState || null,
        options: () => [
          { value: 'ACTIVE', label: t('common.active') },
          { value: 'INACTIVE', label: t('common.inactive') },
        ],
        patch: (value) => ({ objectState: String(value) }),
      },
      type: {
        type: 'select',
        readOnly: isProductBacked,
        value: (li) => li.type || null,
        options: () =>
          LINE_ITEM_TYPES.map((type) => ({
            value: type,
            label: t(`lineItems.types.${type}`, type),
          })),
        patch: (value) => ({ type: String(value) as LineItemType }),
      },
      sponsorshipPercentage: {
        type: 'number',
        min: 0,
        max: 100,
        // Only a sponsorship is delivered as a share of the inventory.
        readOnly: (li) => li.type !== 'SPONSORSHIP',
        value: (li) => li.sponsorshipPercentage ?? null,
        patch: (value) => ({ sponsorshipPercentage: value === null ? 0 : Number(value) }),
      },
    }
    return specs
  }, [t, fallbackCurrency])
}

// ─── Table ─────────────────────────────────────────────────────────────────

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  orderName: 'order.name',
  userState: 'userState',
  objectState: 'objectState',
  type: 'type',
  startDate: 'startDate',
  endDate: 'endDate',
  deliveryPercentage: 'deliveryPercentage',
  sponsorshipPercentage: 'sponsorshipPercentage',
  smoothed: 'smoothed',
  externalReference: 'externalReference',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

/**
 * What the order's line item list can show, in the order the picker lists
 * them. Only rendered is shown without being picked.
 */
const LINE_ITEM_STAT_METRICS = catalogMetrics(
  ['rendered'],
  ['clicks', 'eCpm', 'grossECpm', 'uniqueUsers', 'renderedRate', 'viewability', 'cost']
)

interface LineItemTableProps {
  lineItems: LineItem[]
  loading?: boolean
  emptyMessage?: string
  showAdvertiser?: boolean
  selection?: LineItem[]
  onSelectionChange?: (items: LineItem[]) => void
  /** Offers the fields of the list for editing in place. On by default. */
  editable?: boolean
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
  /** Delivery metrics per line item id — adds the statistics columns when given. */
  stats?: Record<string, LineItemStatsRow>
  /** Totals for the same query, summarised under the list. */
  statsTotals?: LineItemStatsRow
  /**
   * Key the column choice is remembered under. Give a table with extra columns
   * its own key so it does not share the line item list's picked columns.
   */
  columnsKey?: string
}

export function LineItemTable({
  lineItems,
  loading,
  emptyMessage,
  showAdvertiser = true,
  selection,
  onSelectionChange,
  editable = true,
  sort,
  onSortChange,
  stats,
  statsTotals,
  columnsKey = 'lineItems',
}: LineItemTableProps) {
  const { t } = useTranslation()
  const { currentNetwork } = useNetwork()
  const primaryTagId = currentNetwork?.tagId
  const selectable = !!onSelectionChange
  const statLabel = useStatLabel()

  // The currency a price gets when the line item has none of its own yet.
  const fallbackCurrency = useDefaultCurrency({})
  const specs = useLineItemEditSpecs(fallbackCurrency)
  const { edits, setEdit, bar } = useInlineSave<LineItem>({
    save: (rows) =>
      LineItemsApi.updateMany(rows.map(({ id, patch }) => ({ id, ...patch }) as LineItemBulkInput)),
    // The same line item is listed on its own page, under its order and under
    // its advertiser, so every list of them has to be refetched.
    invalidateKeys: ['lineitems', 'order-lineitems', 'advertiser-lineitems', 'lineitem'],
    errorTitle: t('lineItems.saveFailed'),
  })

  // Adnuntius staff check political line items' public notices from the list.
  const currentUser = useCurrentUser()
  const showTransparency = isSuperAdmin(currentUser?.username)

  const columns = useMemo<ColumnDef<LineItem, unknown>[]>(() => [
    ...(selectable ? [selectionColumn<LineItem>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 220, edit: specs.name },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    ...(showAdvertiser ? [{
      accessorKey: 'advertiserName',
      header: t('common.advertiser'),
      meta: { minWidth: 130 },
      cell: ({ row }: { row: { original: LineItem } }) => (
        <div className="flex flex-col gap-0.5">
          <MutedCell value={row.original.advertiserName} />
          {row.original.orderName && (
            <span className="text-[11px] text-(--ad-text-faint)">{row.original.orderName}</span>
          )}
        </div>
      ),
    }] : []),
    {
      id: 'executionState',
      header: t('common.delivery'),
      size: 155,
      accessorFn: (li: LineItem) => deliveryStatusOf(li),
      cell: ({ row }) => <DeliveryStatusBadge status={deliveryStatusOf(row.original)} />,
    },
    {
      id: 'objectives',
      header: t('lineItems.objectives'),
      size: 150,
      enableSorting: false,
      meta: { edit: specs.objectives, align: 'right' },
      cell: ({ row }) => (
        <span className="text-xs font-semibold tabular-nums text-(--ad-text)">
          {formatObjectives(row.original.objectives)}
        </span>
      ),
    },
    {
      id: 'targeting',
      header: t('targeting.title'),
      size: 130,
      enableSorting: false,
      cell: ({ row }) => <LineItemTargetingCell lineItem={row.original} />,
    },
    optionalColumn<LineItem>({
      id: 'objectiveType',
      header: t('lineItems.objectiveType'),
      size: 150,
      enableSorting: false,
      meta: { edit: specs.objectiveType },
      cell: ({ row }) => (
        <MutedCell
          value={t(`lineItems.objectiveTypes.${objectiveTypeOf(row.original)}`, objectiveTypeOf(row.original))}
        />
      ),
    }),
    {
      id: 'timeline',
      header: t('lineItems.campaignPeriod'),
      meta: { minWidth: 140 },
      enableSorting: false,
      cell: ({ row }) => {
        const li = row.original
        return (
          <CampaignTimeline
            startDate={li.startDate}
            endDate={li.endDate}
            executionState={li.executionState}
            deliveryPct={li.deliveryPercentage ?? null}
            elapsedTimePct={li.elapsedTimePercentage ?? null}
          />
        )
      },
    },
    optionalColumn<LineItem>({
      accessorKey: 'userState',
      header: t('userStates.label'),
      size: 130,
      meta: { edit: specs.userState },
      cell: ({ row }) => <DeliveryStatusBadge status={row.original.userState} />,
    }),
    optionalColumn<LineItem>({
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      meta: { edit: specs.objectState },
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    }),
    optionalColumn<LineItem>({
      accessorKey: 'type',
      header: t('common.type'),
      size: 150,
      meta: { edit: specs.type },
      cell: ({ row }) => (
        <MutedCell value={row.original.type ? t(`lineItems.types.${row.original.type}`, row.original.type) : null} />
      ),
    }),
    optionalColumn<LineItem>({
      accessorKey: 'orderName',
      header: t('lineItems.order'),
      meta: { minWidth: 150 },
      cell: ({ row }) => <MutedCell value={row.original.orderName} />,
    }),
    optionalColumn<LineItem>({
      accessorKey: 'startDate',
      header: t('common.startDate'),
      size: 120,
      meta: { edit: specs.startDate },
      cell: ({ row }) => <DateCell value={row.original.startDate} />,
    }),
    optionalColumn<LineItem>({
      accessorKey: 'endDate',
      header: t('common.endDate'),
      size: 120,
      meta: { edit: specs.endDate },
      cell: ({ row }) => <DateCell value={row.original.endDate} />,
    }),
    optionalColumn<LineItem>({
      id: 'cpm',
      header: t('lineItems.cpm'),
      accessorFn: (row) => row.bidSpecification?.cpm?.amount ?? 0,
      size: 110,
      meta: { edit: specs.cpm, align: 'right' },
      cell: ({ row }) => <MoneyCell value={row.original.bidSpecification?.cpm} />,
    }),
    optionalColumn<LineItem>({
      id: 'cpc',
      header: t('lineItems.cpc'),
      accessorFn: (row) => row.bidSpecification?.cpc?.amount ?? 0,
      size: 110,
      meta: { edit: specs.cpc, align: 'right' },
      cell: ({ row }) => <MoneyCell value={row.original.bidSpecification?.cpc} />,
    }),
    optionalColumn<LineItem>({
      accessorKey: 'deliveryPercentage',
      header: t('common.delivered'),
      size: 110,
      meta: { align: 'right' },
      cell: ({ row }) => (
        <NumberCell
          value={row.original.deliveryPercentage != null ? Math.round(row.original.deliveryPercentage) : null}
        />
      ),
    }),
    optionalColumn<LineItem>({
      accessorKey: 'sponsorshipPercentage',
      header: t('lineItems.sponsorshipPercentage'),
      size: 130,
      meta: { edit: specs.sponsorshipPercentage, align: 'right' },
      cell: ({ row }) => <NumberCell value={row.original.sponsorshipPercentage ?? null} />,
    }),
    optionalColumn<LineItem>({
      accessorKey: 'smoothed',
      header: t('lineItems.smoothed'),
      size: 130,
      cell: ({ row }) => <BoolCell value={row.original.smoothed} />,
    }),
    optionalColumn<LineItem>({
      accessorKey: 'externalReference',
      header: t('common.externalReference'),
      size: 140,
      meta: { edit: specs.externalReference },
      cell: ({ row }) => <MutedCell value={row.original.externalReference} />,
    }),
    ...(showTransparency
      ? [
          {
            id: 'transparencyNotice',
            header: t('lineItems.transparencyNotice'),
            size: 240,
            enableSorting: false,
            // Only political line items have a notice to show.
            cell: ({ row }: { row: { original: LineItem } }) =>
              row.original.electionId ? <LineItemTransparencyLink lineItemId={row.original.id} /> : null,
          },
        ]
      : []),
    optionalColumn<LineItem>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<LineItem>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
    // Totals for the period chosen on the order's stats, under their own
    // heading in the column picker.
    ...(stats
      ? statMetricColumns<LineItem>({
          metrics: LINE_ITEM_STAT_METRICS,
          statsFor: (row) => stats[row.id],
          label: statLabel,
          totals: statsTotals,
          section: t('stats.statistics'),
        })
      : []),
  ], [t, selectable, showAdvertiser, specs, primaryTagId, stats, statLabel, statsTotals, showTransparency])

  return (
    <>
      <DataTable
        columns={columns}
        data={lineItems}
        loading={loading}
        emptyMessage={emptyMessage ?? t('lineItems.noLineItemsFound')}
        columnsKey={columnsKey}
        rowHref={(row) => `/advertising/lineitems/${row.id}`}
        selection={selection}
        onSelectionChange={onSelectionChange}
        edits={editable ? edits : undefined}
        onEditChange={editable ? setEdit : undefined}
        serverSort={
          onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
        }
      />
      <InlineEditBar {...bar} />
    </>
  )
}
